import React, { useState, useRef, useEffect } from 'react';
import { Calendar as CalendarIcon, ChevronLeft, ChevronRight, X } from 'lucide-react';

export interface CustomCalendarProps {
  label?: string;
  placeholder?: string;
  value?: string; // Stored format: "YYYY-MM-DD"
  onChange: (dateStr: string) => void;
  required?: boolean;
  minDate?: string; // "YYYY-MM-DD"
  maxDate?: string; // "YYYY-MM-DD"
  disablePast?: boolean; // Default true: disables past dates before today
  error?: string;
  className?: string;
}

const MONTH_NAMES = [
  'January',
  'February',
  'March',
  'April',
  'May',
  'June',
  'July',
  'August',
  'September',
  'October',
  'November',
  'December',
];

const WEEKDAYS = ['Su', 'Mo', 'Tu', 'We', 'Th', 'Fr', 'Sa'];

export const CustomCalendar: React.FC<CustomCalendarProps> = ({
  label,
  placeholder = 'Select Date',
  value,
  onChange,
  required = false,
  minDate,
  maxDate,
  disablePast = true,
  error,
  className = '',
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [viewMode, setViewMode] = useState<'days' | 'months' | 'years'>('days');
  const containerRef = useRef<HTMLDivElement>(null);

  const getTodayStr = (): string => {
    const now = new Date();
    const y = now.getFullYear();
    const m = String(now.getMonth() + 1).padStart(2, '0');
    const d = String(now.getDate()).padStart(2, '0');
    return `${y}-${m}-${d}`;
  };

  const todayStr = getTodayStr();
  const effectiveMinDate = disablePast
    ? (minDate && minDate > todayStr ? minDate : todayStr)
    : minDate;

  // Parse initial view date
  const parseDateStr = (dateStr?: string): Date => {
    if (!dateStr) return new Date();
    const parts = dateStr.split('-');
    if (parts.length === 3) {
      const y = parseInt(parts[0], 10);
      const m = parseInt(parts[1], 10) - 1;
      const d = parseInt(parts[2], 10);
      if (!isNaN(y) && !isNaN(m) && !isNaN(d)) {
        return new Date(y, m, d);
      }
    }
    const parsed = new Date(dateStr);
    return isNaN(parsed.getTime()) ? new Date() : parsed;
  };

  const [viewDate, setViewDate] = useState<Date>(() => parseDateStr(value));
  const [prevValue, setPrevValue] = useState<string | undefined>(value);

  if (value !== prevValue) {
    setPrevValue(value);
    setViewDate(parseDateStr(value));
  }

  // Click outside to close popover
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (containerRef.current && !containerRef.current.contains(event.target as Node)) {
        setIsOpen(false);
        setViewMode('days');
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const year = viewDate.getFullYear();
  const month = viewDate.getMonth();

  const now = new Date();
  const currentYear = now.getFullYear();
  const currentMonth = now.getMonth();
  const isPrevMonthDisabled = disablePast && (year < currentYear || (year === currentYear && month <= currentMonth));

  // Helper date formatting for input trigger display (e.g., 24/09/2026)
  const formatDisplayDate = (val?: string): string => {
    if (!val) return '';
    const parts = val.split('-');
    if (parts.length === 3) {
      const [y, m, d] = parts;
      if (y && m && d) return `${d}/${m}/${y}`;
    }
    return val;
  };

  const handlePrevMonth = () => {
    if (isPrevMonthDisabled) return;
    setViewDate(new Date(year, month - 1, 1));
  };

  const handleNextMonth = () => {
    setViewDate(new Date(year, month + 1, 1));
  };

  const handleSelectDay = (dayNum: number) => {
    const selectedYear = year;
    const selectedMonth = String(month + 1).padStart(2, '0');
    const selectedDay = String(dayNum).padStart(2, '0');
    const resultStr = `${selectedYear}-${selectedMonth}-${selectedDay}`;

    onChange(resultStr);
    setIsOpen(false);
  };

  const handleTodayClick = () => {
    const now = new Date();
    const y = now.getFullYear();
    const m = String(now.getMonth() + 1).padStart(2, '0');
    const d = String(now.getDate()).padStart(2, '0');
    setViewDate(now);
    onChange(`${y}-${m}-${d}`);
    setIsOpen(false);
  };

  const handleClearClick = (e: React.MouseEvent) => {
    e.stopPropagation();
    onChange('');
  };

  // Calendar Day Grid Calculation
  const firstDayOfWeek = new Date(year, month, 1).getDay();
  const totalDaysInMonth = new Date(year, month + 1, 0).getDate();
  const totalDaysInPrevMonth = new Date(year, month, 0).getDate();

  const isSelected = (dayNum: number) => {
    if (!value) return false;
    const [vy, vm, vd] = value.split('-').map(Number);
    return vy === year && vm === month + 1 && vd === dayNum;
  };

  const isToday = (dayNum: number) => {
    return now.getFullYear() === year && now.getMonth() === month && now.getDate() === dayNum;
  };

  const isDateDisabled = (dayNum: number) => {
    const currentStr = `${year}-${String(month + 1).padStart(2, '0')}-${String(dayNum).padStart(2, '0')}`;
    if (effectiveMinDate && currentStr < effectiveMinDate) return true;
    if (maxDate && currentStr > maxDate) return true;
    return false;
  };

  return (
    <div className={`space-y-1.5 relative ${className}`} ref={containerRef}>
      {label && (
        <label className="block text-xs font-semibold text-gray-700 mb-1.5">
          {label} {required && <span className="text-rose-500">*</span>}
        </label>
      )}

      {/* Trigger Button */}
      <button
        type="button"
        onClick={() => setIsOpen(!isOpen)}
        className={`w-full flex items-center justify-between border ${
          error
            ? 'border-rose-400 focus:border-rose-500 bg-rose-50/20'
            : 'border-gray-300 focus:border-[#C1272D] focus:ring-1 focus:ring-[#C1272D]'
        } outline-none text-gray-700 text-xs md:text-sm rounded-xl py-2.5 px-3.5 bg-white cursor-pointer hover:border-gray-400 transition-colors shadow-2xs`}
      >
        <span className={`truncate font-semibold ${value ? 'text-slate-800' : 'text-slate-400'}`}>
          {value ? formatDisplayDate(value) : placeholder}
        </span>

        <div className="flex items-center gap-1.5 shrink-0 text-slate-400">
          {value && !required && (
            <span
              onClick={handleClearClick}
              className="p-1 hover:text-rose-600 rounded-full hover:bg-rose-50 transition-colors"
              title="Clear date"
            >
              <X className="w-3.5 h-3.5" />
            </span>
          )}
          <CalendarIcon className="w-4 h-4 text-slate-400" />
        </div>
      </button>

      {error && <span className="text-rose-500 text-xs font-bold block">{error}</span>}

      {/* Floating Calendar Popover */}
      {isOpen && (
        <div className="absolute z-50 mt-1.5 left-0 w-72 sm:w-80 bg-white rounded-2xl border border-slate-200 shadow-2xl p-4 space-y-3 select-none animate-fadeIn">
          {/* Calendar Header Bar */}
          <div className="flex items-center justify-between border-b border-slate-100 pb-3 px-1">
            <button
              type="button"
              disabled={isPrevMonthDisabled}
              onClick={handlePrevMonth}
              className={`p-1.5 rounded-lg text-slate-600 transition-colors ${
                isPrevMonthDisabled
                  ? 'opacity-25 pointer-events-none cursor-not-allowed text-slate-300'
                  : 'hover:bg-slate-100 cursor-pointer'
              }`}
              title="Previous Month"
            >
              <ChevronLeft className="w-4 h-4" />
            </button>

            <button
              type="button"
              onClick={() => setViewMode(viewMode === 'days' ? 'months' : 'days')}
              className="text-xs md:text-sm font-extrabold text-slate-800 hover:text-[#C1272D] transition-colors cursor-pointer flex items-center gap-1 px-2 py-1 rounded-lg hover:bg-slate-50"
            >
              <span>{MONTH_NAMES[month]} {year}</span>
            </button>

            <button
              type="button"
              onClick={handleNextMonth}
              className="p-1.5 rounded-lg hover:bg-slate-100 text-slate-600 transition-colors cursor-pointer"
              title="Next Month"
            >
              <ChevronRight className="w-4 h-4" />
            </button>
          </div>

          {/* Days View */}
          {viewMode === 'days' && (
            <div>
              {/* Weekdays Header */}
              <div className="grid grid-cols-7 gap-1 text-center mb-1">
                {WEEKDAYS.map((wd) => (
                  <span key={wd} className="text-[11px] font-bold text-slate-400">
                    {wd}
                  </span>
                ))}
              </div>

              {/* Days Matrix */}
              <div className="grid grid-cols-7 gap-1 text-center">
                {/* Previous month padding days */}
                {Array.from({ length: firstDayOfWeek }).map((_, idx) => {
                  const prevDayNum = totalDaysInPrevMonth - firstDayOfWeek + idx + 1;
                  return (
                    <span
                      key={`prev-${idx}`}
                      className="text-xs py-2 text-slate-300 font-medium cursor-default"
                    >
                      {prevDayNum}
                    </span>
                  );
                })}

                {/* Current month days */}
                {Array.from({ length: totalDaysInMonth }).map((_, idx) => {
                  const dayNum = idx + 1;
                  const active = isSelected(dayNum);
                  const today = isToday(dayNum);
                  const disabled = isDateDisabled(dayNum);

                  return (
                    <button
                      key={`day-${dayNum}`}
                      type="button"
                      disabled={disabled}
                      onClick={() => handleSelectDay(dayNum)}
                      className={`text-xs py-2 rounded-xl font-bold transition-all cursor-pointer ${
                        active
                          ? 'bg-[#C1272D] text-white shadow-xs'
                          : today
                          ? 'border border-[#C1272D] text-[#C1272D] bg-rose-50/40 hover:bg-[#C1272D] hover:text-white'
                          : 'text-slate-700 hover:bg-rose-50 hover:text-[#C1272D]'
                      } ${disabled ? 'opacity-25 pointer-events-none text-slate-400' : ''}`}
                    >
                      {dayNum}
                    </button>
                  );
                })}
              </div>
            </div>
          )}

          {/* Months Switcher View */}
          {viewMode === 'months' && (
            <div className="grid grid-cols-3 gap-2 py-2">
              {MONTH_NAMES.map((mName, mIdx) => {
                const isMonthDisabled =
                  disablePast && (year < currentYear || (year === currentYear && mIdx < currentMonth));
                return (
                  <button
                    key={mName}
                    type="button"
                    disabled={isMonthDisabled}
                    onClick={() => {
                      setViewDate(new Date(year, mIdx, 1));
                      setViewMode('days');
                    }}
                    className={`py-2 text-xs font-bold rounded-xl transition-all ${
                      mIdx === month
                        ? 'bg-[#C1272D] text-white'
                        : isMonthDisabled
                        ? 'opacity-25 pointer-events-none text-slate-400 bg-slate-50'
                        : 'bg-slate-50 text-slate-700 hover:bg-rose-50 hover:text-[#C1272D]'
                    }`}
                  >
                    {mName.slice(0, 3)}
                  </button>
                );
              })}
            </div>
          )}

          {/* Calendar Footer Actions */}
          <div className="flex items-center justify-between pt-2 border-t border-slate-100 text-xs">
            <button
              type="button"
              onClick={handleTodayClick}
              className="text-[#C1272D] font-extrabold hover:underline cursor-pointer"
            >
              Today
            </button>
            <button
              type="button"
              onClick={() => setIsOpen(false)}
              className="text-slate-500 font-semibold hover:text-slate-800 cursor-pointer"
            >
              Close
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default CustomCalendar;
